// src/pages/ImportScreeningsPage.jsx
// Route: /ctc-screenings/import — ADMIN / HEALTH_NAVIGATOR only.
//
// Bulk CTC screening import from a camp .xlsx sheet. A near-copy of
// ImportPatientsPage.jsx (same 20MB/.xlsx guard, role gate and result summary),
// with two differences:
//   - The upload zone uses the shared .dropzone class rather than inline styles.
//   - The result has no "Updated" count. The screening importer is CREATE-only
//     (phone is not unique across screenings, and re-screening the same person
//     at a later camp is a legitimate new record). A re-uploaded sheet's rows
//     come back as skipped duplicates, not updates.

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { ctcScreeningAPI } from '../services/api';
import Icon from '../components/Icon';

const MAX_FILE_MB = 20;
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const BREADCRUMBS = [{ label: 'CTC Screenings', to: '/ctc-screenings' }, { label: 'Import Screenings' }];

function isXlsxFile(f) {
  if (!f) return false;
  if (f.type === XLSX_MIME) return true;
  return /\.xlsx$/i.test(f.name || '');
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ImportScreeningsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAllowed = ['ADMIN', 'HEALTH_NAVIGATOR'].includes(user?.role);

  const fileInputRef = useRef(null);

  const [file,      setFile]      = useState(null);
  const [dragOver,  setDragOver]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState('');
  const [result,    setResult]    = useState(null); // { created, skipped, errors }

  if (!isAllowed) {
    return (
      <AppShell breadcrumbs={BREADCRUMBS}>
        <div className="empty-state" style={{ paddingTop: 80 }}>
          <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="lock" size={20} /></div>
          <div className="empty-title">Access Required</div>
          <div className="empty-desc">You need admin or health navigator privileges to import screenings.</div>
        </div>
      </AppShell>
    );
  }

  function handleFileSelect(f) {
    if (!f) return;
    if (!isXlsxFile(f)) {
      setError('Please select an .xlsx file — other formats are not supported.');
      setFile(null);
      return;
    }
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`File too large — maximum ${MAX_FILE_MB}MB`);
      setFile(null);
      return;
    }
    setFile(f);
    setError('');
    setResult(null);
  }

  async function handleImport() {
    if (!file) { setError('Please select a file first.'); return; }
    setError('');
    setUploading(true);
    setResult(null);
    try {
      const res = await ctcScreeningAPI.importXlsx(file);
      setResult(res.data);
      setFile(null);
    } catch (e) {
      setError(e.response?.data?.message || 'Import failed — please try again.');
    } finally {
      setUploading(false);
    }
  }

  const openPicker = () => !uploading && fileInputRef.current?.click();

  const hasResult = !!result;
  const errors = (hasResult && Array.isArray(result.errors)) ? result.errors : [];
  const hasErrors = errors.length > 0;

  return (
    <AppShell breadcrumbs={BREADCRUMBS}>
      <span className="back-link fade-in-up" onClick={() => navigate('/ctc-screenings')}>
        ← Back to CTC Screenings
      </span>

      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">Import Screenings</div>
          <div className="page-subtitle">
            Bulk-create screening records from a camp .xlsx sheet — every valid row becomes a new record
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error"><Icon name="alert" size={14} /> {error}</div>}

      <div className="card fade-in-up delay-1">
        <div
          role="button"
          tabIndex={0}
          className={`dropzone${dragOver ? ' dragover' : ''}`}
          style={{ marginBottom: 20, cursor: uploading ? 'default' : 'pointer' }}
          onClick={openPicker}
          onKeyDown={e => e.key === 'Enter' && openPicker()}
          onDragOver={e => { e.preventDefault(); if (!uploading) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => {
            e.preventDefault();
            setDragOver(false);
            if (!uploading) handleFileSelect(e.dataTransfer.files[0]);
          }}
        >
          <div className="dropzone__icon" aria-hidden="true">
            <Icon name={file ? 'chart' : 'paperclip'} size={26} />
          </div>
          {file ? (
            <div className="dropzone__text">
              <strong>{file.name}</strong><br />
              {formatSize(file.size)}{!uploading && ' — click to change file'}
            </div>
          ) : (
            <div className="dropzone__text">
              <strong>Click to select a spreadsheet</strong> or drag and drop here<br />
              .xlsx only — up to {MAX_FILE_MB}MB, in the CTC screening import template's column order
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          style={{ display: 'none' }}
          onChange={e => handleFileSelect(e.target.files[0])}
        />

        <button
          type="button"
          className="btn btn-primary"
          disabled={!file || uploading}
          onClick={handleImport}
        >
          {uploading ? 'Importing…' : 'Import'}
        </button>
      </div>

      {hasResult && (
        <div className="fade-in-up delay-1">
          <div className="stats-row" style={{ gridTemplateColumns: 'repeat(2,1fr)', marginTop: 20 }}>
            <div className="stat-card">
              <div className="stat-label">Created</div>
              <div className="stat-value">{result.created ?? 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Skipped</div>
              <div className="stat-value" style={result.skipped > 0 ? { color: 'var(--red)' } : undefined}>{result.skipped ?? 0}</div>
              <div className="stat-change">errors and duplicates of existing records</div>
            </div>
          </div>

          {/* Not just skipped rows: the importer also reports non-fatal issues
              ("… — field left blank") on rows it still created, so this list
              can be longer than the Skipped count.

              KNOWN ISSUE (backend, pre-existing): `row` is POI's 0-based sheet
              index, so the first data row — row 2 in Excel — is reported as
              row 1. PatientImportService does exactly the same, so this is
              shown as-is to match Import Patients; compensating here would go
              wrong the day the backend is fixed. The fix is one line in each
              importer. */}
          {hasErrors ? (
            <div className="card" style={{ marginTop: 20 }}>
              <div className="page-subtitle" style={{ marginBottom: 12 }}>
                Row issues ({errors.length})
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((e, i) => (
                      <tr key={`${e.row}-${i}`}>
                        <td>{e.row}</td>
                        <td>{e.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="alert alert-success" style={{ marginTop: 20 }}>
              ✓ Import complete — no row issues.
            </div>
          )}

          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => navigate('/ctc-screenings')}>
              View CTC Screenings →
            </button>
          </div>
        </div>
      )}
    </AppShell>
  );
}
